import { useId, useRef, useState, type KeyboardEvent } from 'react';

export interface SuggestionAutocomplete {
  /** Identifiant stable de la suggestion — sert à la retrouver après un clic ou une touche. */
  cle: string;
  /** Ce qui s'affiche dans la liste et, une fois choisi, dans le champ. */
  libelle: string;
}

/**
 * Un champ texte qui propose une liste, sans jamais l'imposer.
 *
 * `<datalist>` a été écarté : chaque navigateur y filtre à sa façon — certains en simple
 * sous-chaîne, d'autres en tenant compte des accents et d'autres non — et rien ne permet
 * de reprendre l'ordre déjà décidé par `suggestCountries`/`suggestCommunes` (le pays le
 * plus probable en tête, la commune la plus peuplée en tête). Ce composant refait donc à
 * la main le motif « combobox » de l'ARIA Authoring Practices — un champ texte, une liste
 * déroulante qui le suit, les flèches pour la parcourir — plutôt que de suivre le
 * comportement, non garanti, d'un élément natif.
 *
 * **Rien n'oblige à choisir dans la liste.** Le texte tapé reste la valeur, que la liste
 * propose une correspondance ou non — c'est essentiel pour la ville, où toute commune de
 * moins de 2 000 habitants restera un texte libre, jamais une erreur de saisie.
 */
export function ChampAutocomplete({
  label,
  valeur,
  onSaisie,
  suggestions,
  onChoisir,
  aide,
  autoComplete,
}: {
  label: string;
  valeur: string;
  onSaisie: (texte: string) => void;
  suggestions: SuggestionAutocomplete[];
  onChoisir: (suggestion: SuggestionAutocomplete) => void;
  aide?: string;
  autoComplete?: string;
}) {
  const id = useId();
  const listeId = `${id}-liste`;
  const aideId = `${id}-aide`;
  const [ouvert, setOuvert] = useState(false);
  const [survole, setSurvole] = useState(0);
  // Un clic sur une option déclenche d'abord le flou du champ, qui fermerait la liste
  // avant que le clic n'atteigne son option. `onMouseDown` capture le choix un instant
  // plus tôt que `onClick`, avant que le flou n'ait eu lieu.
  const enTrainDeChoisir = useRef(false);

  const visible = ouvert && suggestions.length > 0;
  const indexBorne = Math.min(survole, Math.max(suggestions.length - 1, 0));

  function choisir(suggestion: SuggestionAutocomplete): void {
    onChoisir(suggestion);
    setOuvert(false);
  }

  function surTouche(evenement: KeyboardEvent<HTMLInputElement>): void {
    if (!visible) {
      if (evenement.key === 'ArrowDown' && suggestions.length > 0) {
        setOuvert(true);
        setSurvole(0);
      }
      return;
    }
    if (evenement.key === 'ArrowDown') {
      evenement.preventDefault();
      setSurvole((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (evenement.key === 'ArrowUp') {
      evenement.preventDefault();
      setSurvole((i) => Math.max(i - 1, 0));
    } else if (evenement.key === 'Enter') {
      const choisie = suggestions[indexBorne];
      if (choisie) {
        evenement.preventDefault();
        choisir(choisie);
      }
    } else if (evenement.key === 'Escape') {
      setOuvert(false);
    }
  }

  return (
    <div className="champ champ-autocomplete">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={visible}
        aria-controls={listeId}
        aria-activedescendant={visible ? `${listeId}-${indexBorne}` : undefined}
        aria-describedby={aide ? aideId : undefined}
        autoComplete={autoComplete ?? 'off'}
        value={valeur}
        onChange={(evenement) => {
          onSaisie(evenement.target.value);
          setOuvert(true);
          setSurvole(0);
        }}
        onFocus={() => suggestions.length > 0 && setOuvert(true)}
        onBlur={() => {
          if (!enTrainDeChoisir.current) setOuvert(false);
          enTrainDeChoisir.current = false;
        }}
        onKeyDown={surTouche}
      />
      {visible ? (
        <ul className="champ-autocomplete-liste" role="listbox" id={listeId}>
          {suggestions.map((suggestion, index) => (
            <li
              key={suggestion.cle}
              id={`${listeId}-${index}`}
              role="option"
              aria-selected={index === indexBorne}
              className={index === indexBorne ? 'survolee' : undefined}
              onMouseDown={() => {
                enTrainDeChoisir.current = true;
              }}
              onMouseEnter={() => setSurvole(index)}
              onClick={() => choisir(suggestion)}
            >
              {suggestion.libelle}
            </li>
          ))}
        </ul>
      ) : null}
      {aide ? (
        <p className="aide" id={aideId}>
          {aide}
        </p>
      ) : null}
    </div>
  );
}
